import gp, { JsonPointer, splitLast } from '@sagold/json-pointer';
import {
  Change,
  deepEqual,
  Editor,
  getData,
  getNodeList,
  isJsonError,
  JsonSchema,
  Node,
  Plugin,
  PluginEvent,
  PluginInstance,
  StringNode,
  updateNode,
} from '@sagold/react-json-editor';
import { v4 as uuid } from 'uuid';

const SCHEMA_KEY = 'conditionalFilter' as keyof JsonSchema;

type TableFilter = { [p: string]: string | string[] | null };
export type Options = {
  values: string[];
  labels: (string | undefined)[];
};

export type FetchOptionsFunction = (filter: TableFilter) => Promise<{
  [p: keyof TableFilter]: Options;
}>;

/**
 * json-schema option, e.g.
 * {
 *   type: "string",
 *   enum: [],
 *   conditionalFilter: {
 *      dataProperty: "fundId"
 *   }
 * }
 */
export type SchemaOptions = {
  dataProperty: string;
  dataGroup?: string;
};

/**
 * Add async conditional filtering to specified string-selections.
 * @todo extend functionality to work on conditionalFilter groups: `dataGroup`. Currently only supports a single group
 *
 * in json-editor pass an option
 *
 * ```
 * {
 *   pluginOptions: {
 *      conditionalFilter: {
 *        fetchOptions: typeof FetchOptionsFunction
 *     }
 *   },
 *   plugins: [ConditionalFilterPlugin]
 * }
 * ```
 *
 * for resolving dynamic select-options and register the corresponding input
 * elements with
 *
 * ```
 * {
 *   title: "Fund ID ",
 *   type: "string",
 *   enum: []
 *   conditionalFilter: {
 *     dataProperty: "fundId"
 *   }
 * }
 * ```
 */
export const ConditionalFilterPlugin: Plugin<{
  fetchOptions?: FetchOptionsFunction;
}> = (he, { fetchOptions }) => {
  if (typeof fetchOptions !== 'function') {
    console.log(
      `ConditionalFilterPlugin: no callback 'fetchOptions' passed in 'pluginOptions.${SCHEMA_KEY}' `
    );
    return undefined;
  }

  // fetch schema definitions for conditional filtering
  const conditonalSchemaLocations: JsonPointer[] = [];
  he.draft.eachSchema((schema, pointer) => {
    if (schema[SCHEMA_KEY]) {
      conditonalSchemaLocations.push(pointer);
    }
  });

  if (conditonalSchemaLocations.length === 0) {
    console.log(
      `ConditionalFilterPlugin: no definitions found for '${SCHEMA_KEY}'`
    );
    return undefined;
  }

  // cache last requested filter for change-detection
  let lastFilter: TableFilter;

  // Information to handle async events
  const pluginId = uuid();
  let latestPendingRequestId: string;

  function removeDuplicatesFromPropertyOptions(
    propertyOptions?: Options
  ): Options | undefined {
    if (propertyOptions == null) {
      return propertyOptions;
    }
    const values: Options['values'] = [];
    const labels: Options['labels'] = [];
    for (const [index, value] of propertyOptions.values.entries()) {
      if (propertyOptions.values.indexOf(value) === index) {
        values.push(propertyOptions.values[index]);
        labels.push(propertyOptions.labels[index]);
      }
    }
    return {
      ...propertyOptions,
      values,
      labels,
    };
  }

  async function updateConditionalFilter(filter: TableFilter, root: Node) {
    setLoadingState(root, true);
    try {
      const requestId = uuid();
      latestPendingRequestId = requestId;

      const options = await fetchOptions?.(filter);

      // We only care about the results of the latest request being sent, because otherwise we might show outdated data
      // Scenario:
      // 1. Request: filter: {}, takes 20 seconds
      // 2. Request: filter: {fund: 1}, takes 5 seconds
      // The second request could be sent automatically e.g. when presetting a form with values. If we wouldn't check
      // whether the current response is from the latest request, we would show correct data after 5 seconds and then
      // overwrite it with incorrect data after 15 seconds. With the check below, the first request will be discarded,
      // no matter when the response was received.
      if (requestId !== latestPendingRequestId) {
        return;
      }

      // Ensure that the plugin is still registered to the editor
      // @todo: Find better ways to unregister plugins, that run async event listeners
      const registeredPluginId = he.plugins?.find(
        (p) => p.id === 'ConditionalFilterPlugin'
      )?.pluginId;

      if (registeredPluginId !== pluginId) {
        return;
      }

      const conditionalNodes = setLoadingState(root, false);
      const jsonSchema = he.draft.getSchema();

      // modify original json-schema
      for (const pointer of conditonalSchemaLocations) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const schema = gp.get<any>(jsonSchema, pointer);
        // @todo: fixme: options is not required on the schema, but this actually makes it required.
        schema.options = {
          ...schema.options,
          loading: false
        };

        const property = schema[SCHEMA_KEY].dataProperty as string;
        const propertyOptions = removeDuplicatesFromPropertyOptions(
          options?.[property]
        );

        if (propertyOptions) {
          if (schema.type === 'string') {
            schema.enum = propertyOptions.values;
            schema.options = schema.options ?? {};
            // allow option duplicates to keep option indices in sync with values, i.e. Distribution Notice (1)
            schema.options.enum = propertyOptions.labels.map(
              (label, idx, arr) =>
                arr.indexOf(label) === idx || label === undefined
                  ? label
                  : `${label} (${arr.filter((l) => l === label).indexOf(label) + 1})`
            );
          } else if (
            schema.type === 'array' &&
            schema.items?.type === 'string'
          ) {
            // find the pointer to the actual editor field
            const [, conditionalNodePointer] = splitLast(pointer);
            const conditionalNode = conditionalNodes.find(
              (n) => n.pointer === `#/${conditionalNodePointer}`
            );
            // remove values that are no longer available
            // rje updateNode doesn't perform any checks and will still use values that are not available in options
            if (conditionalNode) {
              if (schema.options?.selectAll) {
                he.setData({
                  ...he.getData(),
                  [conditionalNode.property]: propertyOptions.values,
                });
              } else {
                const previousValue = getData(conditionalNode) as string[];
                if (Array.isArray(previousValue)) {
                  const updatedValue = previousValue.filter((val) =>
                    propertyOptions.values.includes(val)
                  );
                  he.setValue(conditionalNode.pointer, updatedValue);
                }
              }
            }

            schema.items.enum = propertyOptions.values;
            schema.items.options = schema.options ?? {};
            // allow option duplicates to keep option indices in sync with values, i.e. Distribution Notice (1)
            schema.items.options.enum = propertyOptions.labels.map(
              (label, idx, arr) =>
                arr.indexOf(label) === idx || label === undefined
                  ? label
                  : `${label} (${arr.filter((l) => l === label).indexOf(label) + 1})`
            );
          } else {
            console.log(
              `ConditionalFilterPlugin: unsupported schema for ${pointer}`,
              schema
            );
          }

          // Write changes back to schema
          gp.set(jsonSchema, pointer, schema);
        }
      }

      // Write schema back to editor
      if (jsonSchema != null && !isJsonError(jsonSchema)) {
        he.setSchema(jsonSchema);
      }

      // update nodes
      const oldState = he.getNode();
      const [newState, changes] = recreateNodes(
        he.draft,
        oldState,
        conditionalNodes
      );
      // set state and run update
      he.runPlugins(oldState, newState, changes);
      he.validate();
    } catch (e) {
      console.log('ConditionalFilterPlugin: update failed', e);
      setLoadingState(root, false);
      he.validate();
    }
  }

  updateConditionalFilter({}, he.getNode());

  // @todo initial selection request

  const plugin: PluginInstance = {
    // id can be used to retrieve this plugin instance with editor.plugin("ConditionalFilterPlugin")
    id: 'ConditionalFilterPlugin',
    pluginId,
    onEvent(root: Node, event: PluginEvent) {
      // @ts-expect-error we make a general test for the schema, ignoreing event type
      if (event.type !== 'done' && !event.node?.schema?.[SCHEMA_KEY]) {
        return undefined;
      }
      // get nodes for update
      const conditionalNodes = getNodeList(root).filter(
        (node) => node.schema[SCHEMA_KEY]
      );

      // build filter-set for request
      const dataFilter: TableFilter = {};
      for (const node of conditionalNodes) {
        if (node?.schema?.options?.skipInFilter) {
          continue;
        }
        const filterOptions = node.schema[SCHEMA_KEY] as SchemaOptions;
        if (filterOptions?.dataProperty && filterOptions?.dataGroup) {
          const property = filterOptions.dataProperty;
          // based on json-schema support we expect specific values here. Still,
          // there could be a data-input error so we:
          // @todo check if there are other possible data types that we need to support
          const data = getData(node);

          dataFilter[property] =
            typeof data === 'string' ||
            (Array.isArray(data) && data.every((d) => typeof d === 'string'))
              ? (getData(node) as string | string[])
              : [];

          // getData yields empty arrays for empty selections.
          // for portal-api an empty array is not equal to no filter, so we remove empty arrays here.
          if (
            Array.isArray(dataFilter[property]) &&
            !dataFilter[property]?.length
          ) {
            delete dataFilter[property];
          }
        }
      }

      // if filter set has not changed abort
      if (deepEqual(lastFilter, dataFilter)) {
        return undefined;
      }
      lastFilter = dataFilter;
      updateConditionalFilter(dataFilter, root);
    },
  };
  return plugin;
};

function setLoadingState(root: Node, loading: boolean) {
  return getNodeList(root)
    .filter((node) => node.schema[SCHEMA_KEY])
    .map((node) => {
      node.options = node.options ?? {};
      node.options.loading = loading;
      return node;
    }) as StringNode[];
}

/**
 * recreate multiple nodes
 */
export function recreateNodes(
  draft: Editor['draft'],
  state: Node,
  nodes: StringNode[]
): [Node, Change[]] {
  let currentState = state;
  const changes: Change[] = [];
  for (const node of nodes) {
    const [result, _changes] = updateNode(draft, currentState, node.pointer);
    if (isJsonError(result)) {
      console.log(`ConditionalFilter: recreate node failed ${result.message}`);
      continue;
    }
    currentState = result;
    changes.push(...(_changes ?? []));
  }
  return [currentState, changes];
}
